/**
 * Maps DB permission/group names to the new UI/Excel naming.
 *
 * IMPORTANT:
 * - DB permission IDs/keys are never changed.
 * - Unknown permissions fall back to their original DB name.
 * - Unknown groups fall back to their original DB group name.
 */

export const PERMISSION_NAME_MAP: Record<string, string> = {
    // -------------------------
    // Manage Dashboard
    // -------------------------
    generate_dashboard_executive_summary: 'Executive Summary',
    lms: 'View Training',
    view_dashboard_my_results: 'View My Results',
    view_dashboard_overview: 'View Overview',
    view_dashboard_phishing: 'View Phishing',
    view_dashboard_saq_compliance: 'View SAQ Compliance',
    view_dashboard_scanning: 'View Scanning',
    view_dashboard_spotcheck: 'View Spot Check',
    view_user_activity: 'View User Activity',
  
    // -------------------------
    // Force Sync Management
    // -------------------------
    create_force_sync: 'Create',
  
    // -------------------------
    // Manage Groups
    // -------------------------
    add_group: 'Add Group',
    add_tenant: 'Add Tenant',
    change_group_password: 'Change Group Password',
    delete_group: 'Delete Group',
    delete_tenant: 'Delete Tenant',
    edit_group: 'Edit Group',
    edit_tenant: 'Edit Tenant',
    export_groups: 'Export Groups',
    group_manager_view_page: 'View Group Manager',
    move_group: 'Move Groups',
    restore_group: 'Restore Groups',
    restore_tenant: 'Restore Tenant',
  
    // -------------------------
    // Mapping Settings
    // -------------------------
    edit_mapping_wizard: 'Edit Mapping',
    view_mapping_wizard: 'View Mapping',
  
    // -------------------------
    // Manage Ownership Groups
    // -------------------------
    add_ownership_group: 'Add Ownership Groups',
    delete_ownership_group: 'Delete Ownership Groups',
    edit_ownership_group: 'Edit Ownership Groups',
    export_ownership_groups: 'Export Ownership Groups',
    view_ownership_groups: 'View Ownership Groups',
  
    // -------------------------
    // Reports Management
    // -------------------------
    create_report_current_historical: 'Create Reports',
    view_standard_reports: 'View Reports',
  
    // -------------------------
    // Systems Management
    // -------------------------
    get_system_by_id: 'Get Systems by ID',
    get_systems: 'Get Systems',
  
    // -------------------------
    // Manage System Roles
    // -------------------------
    add_system_role: 'Add System Role',
    clone_system_role: 'Clone System Role',
    delete_system_role: 'Delete System Role',
    edit_system_role: 'Edit System Role',
    export_system_roles: 'Export System Roles',
    system_role_manager_view_page: 'View System Manager',
  
    // -------------------------
    // Manage Users
    // -------------------------
  
    // Activate / Disable
    activate_disable_user__admin: 'Activate / Disable Admins',
    activate_disable_user__manager: 'Activate / Disable Managers',
    activate_disable_user__owner: 'Activate / Disable Owners',
    activate_disable_user__superadmin: 'Activate / Disable Super Admins',
    activate_disable_user__user: 'Activate / Disable Users',
  
    // Add
    add_user__admin: 'Add Admins',
    add_user__manager: 'Add Managers',
    add_user_notification: 'Add User Notifications',
    add_user__owner: 'Add Owners',
    add_user__superadmin: 'Add Super Admins',
    add_user__user: 'Add Users',
  
    // Secondary Role
    assign__Secondary_role: 'Assign Secondary Role',
  
    // Delete
    delete_user__admin: 'Delete Admins',
    delete_user__manager: 'Delete Managers',
    delete_user__owner: 'Delete Owners',
    delete_user__superadmin: 'Delete Super Admins',
    delete_user__user: 'Delete Users',
  
    // Edit
    edit_user__admin: 'Edit Admins',
    edit_user__manager: 'Edit Managers',
    edit_user__owner: 'Edit Owners',
    edit_user__superadmin: 'Edit Super Admins',
    edit_user__user: 'Edit Users',
  
    // Export
    export_user_audit_log: 'Export User Audit Logs',
    export_users: 'Export Users',
  
    // Move
    move_user__admin: 'Move Admin',
    move_user__manager: 'Move Manager',
    move_user__user: 'Move User',
  
    // MFA
    reset_2fa__admin: 'Reset Admin MFA',
    reset_2fa__manager: 'Reset Manager MFA',
    reset_2fa__owner: 'Reset Owner MFA',
    reset_2fa__superadmin: 'Reset Super Admin MFA',
    reset_2fa__user: 'Reset User MFA',
  
    // Password
    reset_password__admin: 'Reset Admin Passwords',
    reset_password__manager: 'Reset Manager Passwords',
    reset_password__owner: 'Reset Owner Passwords',
    reset_password__superadmin: 'Reset Super Admin Passwords',
    reset_password__user: 'Reset User Passwords',
  
    // Restore
    restore_user__admin: 'Restore Admins',
    restore_user__manager: 'Restore Managers',
    restore_user__owner: 'Restore Owners',
    restore_user__superadmin: 'Restore Super Admins',
    restore_user__user: 'Restore User',
  
    // View
    user_manager_screen_view: 'View User Manager',
    view_user__admin: 'View Admins',
    view_user_audit_log: 'View User Audit Logs',
    view_user__manager: 'View Managers',
    view_user__owner: 'View Owners',
    view_user__superadmin: 'View Super Admins',
    view_user__user: 'View Users',
  };
  
  
  /**
   * DB group → New Excel/UI group name.
   */
  export const PERMISSION_GROUP_NAME_MAP: Record<string, string> = {
    dashboard_management: 'Manage Dashboard',
    force_sync_management: 'Force Sync Management',
    groups_management: 'Manage Groups',
    mapping_management: 'Mapping Settings',
    ownership_groups_management: 'Manage Ownership Groups',
    reports_management: 'Reports Management',
    system_management: 'Systems Management',
    system_roles_management: 'Manage System Roles',
    users_management: 'Manage Users',
  };
  
  
  /**
   * Returns new display name when mapping exists.
   * Otherwise keeps the original DB permission name.
   */
  export const getPermissionDisplayName = (
    permissionName: string,
  ): string => {
    return PERMISSION_NAME_MAP[permissionName] ?? permissionName;
  };
  
  
  /**
   * Returns new group display name when mapping exists.
   * Otherwise keeps the original DB group name.
   */
  export const getPermissionGroupDisplayName = (
    groupName: string,
  ): string => {
    return PERMISSION_GROUP_NAME_MAP[groupName] ?? groupName;
  };